import React, { useEffect, useMemo, useRef, useState } from 'react'

import classNames from 'classnames'

import { BasicButtonVariant } from '@/Atomic/Atoms/BasicButton'
import { Icon } from '@/Atomic/Atoms/Icon'
import { Button } from '@/Atomic/Molecules/Button'
import { MenuItem, MenuItemProps } from '@/Atomic/Molecules/MenuItem'
import { IconMenuOnClickEvent } from '@/Atomic/Organisms/MenuButton/types'
import { MenuSheet } from '@/Atomic/Organisms/MenuSheet'
import { useIconMenu } from '@/hooks/useIconMenu'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { useSmartPosition } from '@/hooks/useSmartPosition'

import './CreateMenuButton.scss'

export interface CreateMenuButtonProps {
  menuItems: CreateMenuItem[]
  label: string
  disabled?: boolean
  className?: string
  menuClassName?: string
  buttonClassName?: string
  onOpenChange?: (isOpen: boolean) => void
  boundarySelector?: string
  size?: 'sm' | 'md' | 'lg'
  variant?: BasicButtonVariant
}

export type CreateMenuItem = MenuItemProps & { items?: CreateMenuItem[] }

export const CreateMenuButton: React.FC<CreateMenuButtonProps> = ({
  menuItems,
  label,
  disabled,
  className,
  menuClassName,
  buttonClassName,
  onOpenChange,
  boundarySelector,
  size = 'sm',
  variant = 'primary',
}) => {
  const isMobile = useMediaQuery('(max-width: 766px)')
  const { buttonRef, closeMenu, menuIsOpen, toggleMenu } = useIconMenu()
  const { panelRef, position, hasMeasured } = useSmartPosition({
    isOpen: menuIsOpen,
    triggerRef: buttonRef,
    boundarySelector,
    preferHorizontal: 'left',
    preferVertical: 'bottom',
    listenToScroll: true,
  })

  useEffect(() => {
    onOpenChange?.(menuIsOpen)
  }, [menuIsOpen, onOpenChange])

  const close = (evt?: IconMenuOnClickEvent) => {
    closeMenu(evt || (new MouseEvent('click') as IconMenuOnClickEvent))
  }

  const submenuDirection = useMemo(() => {
    return position.endsWith('left') ? 'left' : 'right'
  }, [position])

  const hoverCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  )

  const clearHoverCloseTimer = () => {
    if (hoverCloseTimeoutRef.current) {
      clearTimeout(hoverCloseTimeoutRef.current)
      hoverCloseTimeoutRef.current = null
    }
  }

  useEffect(() => {
    return () => clearHoverCloseTimer()
  }, [])

  // Mobile drill-in stack (root -> submenu -> ...)
  const [stack, setStack] = useState<CreateMenuItem[][]>([menuItems])

  const currentMobileItems = stack[stack.length - 1] || []

  // Desktop nested hover/click path
  const [openPath, setOpenPath] = useState<number[]>([])

  const resetPanels = () => {
    setStack([menuItems])
    setOpenPath([])
  }

  const onTriggerClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (disabled) return
    if (!menuIsOpen) {
      resetPanels()
    }
    toggleMenu()
  }

  const isPathOpen = (path: number[], candidate = openPath) => {
    if (candidate.length < path.length) return false
    for (let i = 0; i < path.length; i++) {
      if (candidate[i] !== path[i]) return false
    }
    return true
  }

  const renderDesktopItems = (
    items: CreateMenuItem[],
    depth: number,
    pathPrefix: number[],
  ) => {
    return (
      <div className="w-create-menu-button-list">
        {items.map((item, idx) => {
          const path = [...pathPrefix, idx]
          const hasSubmenu = Boolean(item.items && item.items.length > 0)
          const submenuOpen = hasSubmenu && isPathOpen(path)
          const { items: nestedItems, ...menuItemProps } = item

          const onItemClick = (e: React.MouseEvent) => {
            e.preventDefault()
            e.stopPropagation()

            if (hasSubmenu) {
              setOpenPath((prev) =>
                isPathOpen(path, prev) ? prev.slice(0, depth) : path,
              )
              return
            }

            menuItemProps.onClick?.(e)
            close(e.nativeEvent as IconMenuOnClickEvent)
          }

          return (
            <div
              key={`${item.label}-${path.join('-')}`}
              className={classNames('w-create-menu-button-item-wrap', {
                open: submenuOpen,
              })}
              onMouseEnter={() => {
                clearHoverCloseTimer()
                if (hasSubmenu) {
                  setOpenPath(path)
                } else {
                  // Close any open subpanels when hovering a non-nested item.
                  // - depth 0: close all
                  // - depth N: keep ancestors, close deeper panels
                  setOpenPath((prev) => prev.slice(0, depth))
                }
              }}
              onMouseLeave={() => {
                if (!hasSubmenu) return
                // Keep submenu open briefly so the user can move across the gap
                // between the parent item and the subpanel.
                clearHoverCloseTimer()
                hoverCloseTimeoutRef.current = setTimeout(() => {
                  setOpenPath((prev) => prev.slice(0, depth))
                }, 200)
              }}
            >
              <MenuItem
                {...menuItemProps}
                rightIcon={hasSubmenu ? 'chevron-right' : item.rightIcon}
                onClick={onItemClick}
              />

              {hasSubmenu && submenuOpen && (
                <div
                  className={classNames(
                    'w-create-menu-button-subpanel',
                    `sub-${submenuDirection}`,
                  )}
                >
                  {renderDesktopItems(nestedItems!, depth + 1, path)}
                </div>
              )}
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div
      ref={buttonRef}
      className={classNames('w-create-menu-button', className, {
        open: menuIsOpen,
      })}
    >
      <Button
        onClick={onTriggerClick}
        disabled={disabled}
        size={size}
        variant={variant}
        icon="add"
        iconSize="sm"
        iconColor="white"
        buttonClassName={classNames(
          'w-create-menu-button-trigger',
          buttonClassName,
        )}
      >
        <span className="w-create-menu-button-content">
          <span className="w-create-menu-button-label">{label}</span>
          <Icon
            icon="chevron-down"
            size="sm"
            color="white"
            className="w-create-menu-button-chevron"
          />
        </span>
      </Button>

      <MenuSheet
        isOpen={menuIsOpen}
        panelRef={panelRef}
        position={position}
        panelClassName={classNames('w-create-menu-button-menu', menuClassName)}
        panelStyle={{
          visibility: isMobile || hasMeasured ? 'visible' : 'hidden',
        }}
        onBackdropClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          // Previously: when a nested mobile panel is open, tapping the backdrop would "go back"
          // one level instead of closing. Not used right now; until there is a clear UX use-case,
          // the backdrop closes the menu completely.
          //
          // if (isMobile && stack.length > 1) {
          //   setStack((prev) => prev.slice(0, -1))
          //   return
          // }
          close(e.nativeEvent as IconMenuOnClickEvent)
        }}
        onMobileCloseClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          close(e.nativeEvent as IconMenuOnClickEvent)
        }}
      >
        {isMobile ? (
          <div className="w-create-menu-button-list">
            {currentMobileItems.map((item, idx) => {
              const hasSubmenu = Boolean(item.items && item.items.length > 0)
              const { items: nestedItems, ...menuItemProps } = item
              return (
                <MenuItem
                  key={`${item.label}-${idx}`}
                  {...menuItemProps}
                  rightIcon={hasSubmenu ? 'chevron-right' : item.rightIcon}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    if (hasSubmenu) {
                      setStack((prev) => [...prev, nestedItems!])
                      return
                    }
                    menuItemProps.onClick?.(e)
                    close(e.nativeEvent as IconMenuOnClickEvent)
                  }}
                />
              )
            })}
          </div>
        ) : (
          renderDesktopItems(menuItems, 0, [])
        )}
      </MenuSheet>
    </div>
  )
}

export default CreateMenuButton
